<script setup lang="ts">
import { getMetadataText } from '@@/bits/attachments'
import { isAppUsing } from '@@/bits/flip'
import { getPostPropertyFormattedValue } from '@@/bits/post_properties'
import { isPostPinned } from '@@/bits/post_state'
import OzBaseButton, { OzBaseButtonHrefMode } from '@@/library/v4/components/OzBaseButton.vue'
import type { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzBox from '@@/library/v4/components/OzBox.vue'
import OzDivider, { OzDividerColorScheme, OzDividerOrientation } from '@@/library/v4/components/OzDivider.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import type { Cid, Post, WallCustomPostProperty } from '@@/types'
import SurfaceLazy from '@@/vuecomponents/SurfaceLazy.vue'
import SurfaceBeethovenPreview from '@@/vuecomponents/surface_beethoven_preview.vue'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

const props = defineProps<{
  post: Post
  boxColor?: OzBoxColors
  borderRadius?: 0 | 12
  xDivider?: boolean
  fontClass?: string
}>()

defineEmits<{
  (name: 'post-click', postCid: Cid): void
  (name: 'post-dbl-click', postCid: Cid, authorId: number): void
  (name: 'post-hover', postCid: Cid): void
  (name: 'post-unhover', postCid: Cid): void
}>()

const { backgroundLuminance, colorScheme } = storeToRefs(useSurfaceStore())
const { postListPosition } = storeToRefs(useSurfaceMapStore())
const { getLinkDisplayAttributesForUrl } = useSurfaceAttachmentsStore()
const { wallCustomPostProperties } = storeToRefs(useSurfacePostPropertiesStore())

// Strip all tags because we don't want any styles in the post list
const postBody = computed(() => props.post.body ?? '')
const linkDisplayAttributes = computed(() => {
  return props.post.attachment
    ? getLinkDisplayAttributesForUrl(props.post.attachment, props.post.attachment_link)
    : null
})
const linkMetadataText = computed(() =>
  linkDisplayAttributes.value ? getMetadataText(linkDisplayAttributes.value) : '',
)
const xPin = computed(() => isPostPinned(props.post))
const isDarkMode = computed(() => colorScheme.value === 'dark')

const firstPostCustomPropertyValue = computed<any>(() => {
  const postCustomPropertyValues = props.post.custom_properties ?? {}
  if (Object.values(postCustomPropertyValues).length === 0) return ''

  const firstPopulatedWallCustomProperty = wallCustomPostProperties.value
    .filter((property: WallCustomPostProperty) => property.id != null)
    .find((property: WallCustomPostProperty) => postCustomPropertyValues[property.id as string] != null)
  if (firstPopulatedWallCustomProperty == null) return ''

  const firstPopulatedCustomPropertyValue = postCustomPropertyValues[firstPopulatedWallCustomProperty.id as string]
  if (firstPopulatedCustomPropertyValue == null) return ''

  return firstPopulatedWallCustomProperty.data_type === 'link_button'
    ? firstPopulatedCustomPropertyValue
    : getPostPropertyFormattedValue({
        property: firstPopulatedWallCustomProperty,
        value: firstPopulatedCustomPropertyValue,
      })
})

const minPostHeight = 54 // OzBox with only the subject has a minimum height of 54px
const minPostWidth = 304 // SurfaceMapPostListHorizontal.vue has a minimum width of 304px
</script>

<template>
  <OzBox
    :data-post-cid="post.cid"
    :data-testid="`map-post-list-item-${post.id}`"
    :data-rank="post.sort_index"
    :class="[
      'overflow-hidden',
      {
        'p-0': postListPosition === 'bottom',
        'px-2 pt-1': postListPosition === 'side',
      },
    ]"
    :color="boxColor"
    :dark-mode="isDarkMode"
    :radius="borderRadius"
  >
    <OzBaseButton
      :class="[
        'bg-transparent w-full group p-0',
        postListPosition === 'bottom' && 'h-full relative flex items-center justify-center',
      ]"
      :href-mode="OzBaseButtonHrefMode.DisplayOnly"
      :data-bg-luminance="backgroundLuminance"
      :aria-label="__('Reveal this post in the map')"
      :aria-describedby="`${post.cid}-postDescription`"
      @click="$emit('post-click', post.cid)"
      @dblclick="$emit('post-dbl-click', post.cid, Number(post.author_id))"
      @keydown.enter="$emit('post-click', post.cid)"
      @focus="$emit('post-hover', post.cid)"
      @mouseover="$emit('post-hover', post.cid)"
      @blur="$emit('post-unhover', post.cid)"
      @mouseleave="$emit('post-unhover', post.cid)"
    >
      <template v-if="isAppUsing('lazyRenderPosts')">
        <SurfaceLazy tag="span" :min-height="minPostHeight" :min-width="minPostWidth">
          <!-- if post list position is bottom, we want the content to take up a base height of 60px, and only take more space as needed if the content is longer/ has attachment -->
          <!-- to ensure focus visible style looks the same regardless of the content height, we create an absolutely positioned container for showing focus-visible styles -->
          <span
            v-if="postListPosition === 'bottom'"
            :class="[
              'absolute w-[calc(100%-1rem)] h-[calc(100%-1rem)] p-2 rounded-xl box-border z-surface-map-control',
              {
                'group-focus-visible:ring-2': true,
                'group-focus-visible:ring-canary-500': isDarkMode,
                'group-focus-visible:ring-grape-500': !isDarkMode,
              },
            ]"
          />
          <span
            :class="[
              {
                'flex items-center max-w-full text-start': true,
                'hover-hover:hover:bg-overlay-text-bg-light-mode-light-hover': backgroundLuminance === 'light',
                'hover-hover:hover:bg-overlay-text-bg-dark-mode-dark-hover': backgroundLuminance === 'dark',
                'cursor-pointer': true,
                'wish-list-item': true,
              },
              postListPosition === 'side' && {
                'rounded-xl mb-1': true,
                'h-auto': true,
                'px-2 py-3': true,
                'group-focus-visible:ring-2': true,
                'group-focus-visible:ring-canary-500': isDarkMode,
                'group-focus-visible:ring-grape-500': !isDarkMode,
              },
              postListPosition === 'bottom' && {
                'ps-4 pe-3 py-5': true,
                'h-[60px] box-content': true,
              },
              fontClass,
            ]"
          >
            <span :class="['grow flex max-w-full']">
              <span
                :class="[
                  'grow',
                  'max-w-full',
                  'flex flex-col items-start',
                  {
                    'space-y-0': postListPosition === 'bottom',
                    'space-y-1': postListPosition === 'side',
                  },
                  {
                    'w-50': postListPosition === 'bottom',
                    'w-auto': postListPosition === 'side',
                  },
                  'overflow-hidden',
                ]"
              >
                <span
                  v-if="xPin || post.location_name"
                  :id="`${post.cid}-postDescription`"
                  :class="['flex w-full items-center', 'space-s-2']"
                >
                  <OzIcon
                    v-if="xPin"
                    :class="[colorScheme === 'light' && 'text-grape-500', colorScheme === 'dark' && 'text-canary-500']"
                    name="pin_filled"
                    :size="16"
                  />
                  <span v-if="xPin" class="sr-only">{{ __('Pinned post') }}</span>

                  <span
                    v-if="post.location_name"
                    :class="{
                      'text-body-extra-small truncate': true,
                      'text-dark-text-200': colorScheme === 'light',
                      'text-light-text-200': colorScheme === 'dark',
                    }"
                  >
                    {{ post.location_name }}
                  </span>
                </span>

                <span
                  v-if="post.subject"
                  :class="{
                    'text-17-21': postListPosition === 'bottom',
                    'text-body-small': postListPosition === 'side',
                    'line-clamp-2': postListPosition === 'bottom',
                    'line-clamp-1': postListPosition === 'side',
                    'break-word-anywhere': true,
                    'whitespace-break-spaces': true,
                    'text-dark-text-100': colorScheme === 'light',
                    'text-light-text-100': colorScheme === 'dark',
                  }"
                  v-text="post.subject"
                />

                <!-- Fallback when location and subject are both absent -->
                <template v-if="!post.subject && !post.location_name"
                  ><span
                    v-if="postBody"
                    :class="{
                      'text-17-21': postListPosition === 'bottom',
                      'text-body-small': postListPosition === 'side',
                      'line-clamp-2': postListPosition === 'bottom',
                      'line-clamp-1': postListPosition === 'side',
                      'break-word-anywhere': true,
                      'whitespace-break-spaces': true,
                      'text-dark-text-100': colorScheme === 'light',
                      'text-light-text-100': colorScheme === 'dark',
                    }"
                    v-html="postBody"
                  />
                  <!-- Neither subject nor body - there should be an attachment so we display metadata text instead -->
                  <span
                    v-else-if="linkMetadataText"
                    :class="{
                      'text-17-21': postListPosition === 'bottom',
                      'text-body-small': postListPosition === 'side',
                      'line-clamp-2': postListPosition === 'bottom',
                      'line-clamp-1': postListPosition === 'side',
                      'break-word-anywhere': true,
                      'text-dark-text-100': colorScheme === 'light',
                      'text-light-text-100': colorScheme === 'dark',
                    }"
                  >
                    {{ linkMetadataText }}
                  </span>

                  <span
                    v-else
                    :class="{
                      'text-17-21': postListPosition === 'bottom',
                      'text-body-small': postListPosition === 'side',
                      'line-clamp-2': postListPosition === 'bottom',
                      'line-clamp-1': postListPosition === 'side',
                      'break-word-anywhere': true,
                      'text-dark-text-100': colorScheme === 'light',
                      'text-light-text-100': colorScheme === 'dark',
                    }"
                  >
                    {{ firstPostCustomPropertyValue }}
                  </span>
                </template>
              </span>
              <span
                :class="[
                  'shrink-0 flex items-center',
                  {
                    'ms-5': postListPosition === 'bottom',
                    'ms-2': postListPosition === 'side',
                  },
                  {
                    'w-14': postListPosition === 'bottom',
                    'w-10': postListPosition === 'side',
                  },
                ]"
              >
                <span
                  v-if="post.attachment"
                  :class="[
                    'overflow-hidden',
                    'rounded-md',
                    'block',
                    'w-full',
                    {
                      'h-14': postListPosition === 'bottom',
                      'h-10': postListPosition === 'side',
                    },
                  ]"
                >
                  <SurfaceBeethovenPreview
                    :post="post"
                    :url="post.attachment"
                    :width="postListPosition === 'bottom' ? 56 : 40"
                    :height="postListPosition === 'bottom' ? 56 : 40"
                    :aspect-ratio="1"
                    actionable="no"
                    :is-post-preview="true"
                    :attachment-link="post.attachment_link"
                    :custom-thumbnail-url="post.wish_content?.attachment_props?.custom_thumbnail_url"
                  />
                </span>
              </span>
            </span>
          </span>
        </SurfaceLazy>
      </template>
      <template v-else>
        <!-- if post list position is bottom, we want the content to take up a base height of 60px, and only take more space as needed if the content is longer/ has attachment -->
        <!-- to ensure focus visible style looks the same regardless of the content height, we create an absolutely positioned container for showing focus-visible styles -->
        <span
          v-if="postListPosition === 'bottom'"
          :class="[
            'absolute w-[calc(100%-1rem)] h-[calc(100%-1rem)] p-2 rounded-xl box-border z-surface-map-control',
            {
              'group-focus-visible:ring-2': true,
              'group-focus-visible:ring-canary-500': isDarkMode,
              'group-focus-visible:ring-grape-500': !isDarkMode,
            },
          ]"
        />
        <span
          :class="[
            {
              'flex items-center max-w-full text-start': true,
              'hover-hover:hover:bg-overlay-text-bg-light-mode-light-hover': backgroundLuminance === 'light',
              'hover-hover:hover:bg-overlay-text-bg-dark-mode-dark-hover': backgroundLuminance === 'dark',
              'cursor-pointer': true,
              'wish-list-item': true,
            },
            postListPosition === 'side' && {
              'rounded-xl mb-1': true,
              'h-auto': true,
              'px-2 py-3': true,
              'group-focus-visible:ring-2': true,
              'group-focus-visible:ring-canary-500': isDarkMode,
              'group-focus-visible:ring-grape-500': !isDarkMode,
            },
            postListPosition === 'bottom' && {
              'ps-4 pe-3 py-5': true,
              'h-[60px] box-content': true,
            },
            fontClass,
          ]"
        >
          <span :class="['grow flex max-w-full']">
            <span
              :class="[
                'grow',
                'max-w-full',
                'flex flex-col items-start',
                {
                  'space-y-0': postListPosition === 'bottom',
                  'space-y-1': postListPosition === 'side',
                },
                {
                  'w-50': postListPosition === 'bottom',
                  'w-auto': postListPosition === 'side',
                },
                'overflow-hidden',
              ]"
            >
              <span
                v-if="xPin || post.location_name"
                :id="`${post.cid}-postDescription`"
                :class="['flex w-full items-center', 'space-s-2']"
              >
                <OzIcon
                  v-if="xPin"
                  :class="[colorScheme === 'light' && 'text-grape-500', colorScheme === 'dark' && 'text-canary-500']"
                  name="pin_filled"
                  :size="16"
                />
                <span v-if="xPin" class="sr-only">{{ __('Pinned post') }}</span>

                <span
                  v-if="post.location_name"
                  :class="{
                    'text-body-extra-small truncate': true,
                    'text-dark-text-200': colorScheme === 'light',
                    'text-light-text-200': colorScheme === 'dark',
                  }"
                >
                  {{ post.location_name }}
                </span>
              </span>

              <span
                v-if="post.subject"
                :class="{
                  'text-17-21': postListPosition === 'bottom',
                  'text-body-small': postListPosition === 'side',
                  'line-clamp-2': postListPosition === 'bottom',
                  'line-clamp-1': postListPosition === 'side',
                  'break-word-anywhere': true,
                  'whitespace-break-spaces': true,
                  'text-dark-text-100': colorScheme === 'light',
                  'text-light-text-100': colorScheme === 'dark',
                }"
                v-text="post.subject"
              />

              <!-- Fallback when location and subject are both absent -->
              <template v-if="!post.subject && !post.location_name"
                ><span
                  v-if="postBody"
                  :class="{
                    'text-17-21': postListPosition === 'bottom',
                    'text-body-small': postListPosition === 'side',
                    'line-clamp-2': postListPosition === 'bottom',
                    'line-clamp-1': postListPosition === 'side',
                    'break-word-anywhere': true,
                    'whitespace-break-spaces': true,
                    'text-dark-text-100': colorScheme === 'light',
                    'text-light-text-100': colorScheme === 'dark',
                  }"
                  v-html="postBody"
                />

                <!-- Neither subject nor body - there should be an attachment so we display metadata text instead -->
                <span
                  v-else
                  :class="{
                    'text-17-21': postListPosition === 'bottom',
                    'text-body-small': postListPosition === 'side',
                    'line-clamp-2': postListPosition === 'bottom',
                    'line-clamp-1': postListPosition === 'side',
                    'break-word-anywhere': true,
                    'text-dark-text-100': colorScheme === 'light',
                    'text-light-text-100': colorScheme === 'dark',
                  }"
                >
                  {{ linkMetadataText }}
                </span>
              </template>
            </span>
            <span
              :class="[
                'shrink-0 flex items-center',
                {
                  'ms-5': postListPosition === 'bottom',
                  'ms-2': postListPosition === 'side',
                },
                {
                  'w-14': postListPosition === 'bottom',
                  'w-10': postListPosition === 'side',
                },
              ]"
            >
              <span
                v-if="post.attachment"
                :class="[
                  'overflow-hidden',
                  'rounded-md',
                  'block',
                  'w-full',
                  {
                    'h-14': postListPosition === 'bottom',
                    'h-10': postListPosition === 'side',
                  },
                ]"
              >
                <SurfaceBeethovenPreview
                  :post="post"
                  :url="post.attachment"
                  :width="postListPosition === 'bottom' ? 56 : 40"
                  :height="postListPosition === 'bottom' ? 56 : 40"
                  :aspect-ratio="1"
                  actionable="no"
                  :is-post-preview="true"
                  :attachment-link="post.attachment_link"
                  :custom-thumbnail-url="post.wish_content?.attachment_props?.custom_thumbnail_url"
                />
              </span>
            </span>
          </span>
        </span>
      </template>
    </OzBaseButton>
    <template v-if="xDivider">
      <OzDivider
        :class="[postListPosition === 'side' && 'mx-2', 'wish-list-item-divider']"
        :orientation="postListPosition === 'bottom' ? OzDividerOrientation.Vertical : OzDividerOrientation.Horizontal"
        :dark-mode="colorScheme === 'dark'"
        :color-scheme="OzDividerColorScheme.Solid"
      />
    </template>
  </OzBox>
</template>

<style lang="postcss" scoped>
/* https://github.com/SortableJS/Sortable#options */
/* - sortable-ghost: Class name for the drop placeholder */
/* - sortable-drag: Class name for the dragging item */
.wish-list-item[data-bg-luminance='light'] {
  .sortable-ghost &,
  .sortable-drag & {
    /* Should match the hover style: `bg-overlay-text-bg-light-mode-light-hover` */
    background-color: rgba(29, 29, 29, 0.1);
  }
}

.wish-list-item[data-bg-luminance='dark'] {
  .sortable-ghost &,
  .sortable-drag & {
    /* Should match the hover style: `bg-overlay-text-bg-dark-mode-dark-hover` */
    background-color: rgba(255, 255, 255, 0.2);
  }
}

.wish-list-item-divider {
  .sortable-drag & {
    visibility: hidden;
  }
}
</style>
